/* ============================================================================
 * BNE Dynamics — Required Spindle Core auto-add
 * ----------------------------------------------------------------------------
 * Watches cart-modifying requests. When a product tagged "requires-core-XXX"
 * is added to the cart, automatically adds the matching spindle core as a
 * separate line item with quantity equal to the parent line.
 *
 * The core line is locked: it cannot be removed and its quantity cannot be
 * changed by the customer. If a customer somehow removes it (URL hack,
 * theme bug), the script will re-add it on the next cart change or page load.
 *
 * Bookkeeping:
 *   - Each auto-added core carries a hidden line item property
 *     `_auto_added_for: <parent_variant_id>` so admin/Apps Script can tell
 *     auto-added cores from standalone core sales.
 *   - A visible property `Required core: Refundable on return` is shown to
 *     the customer so the policy is clear.
 *
 * To add a new mapping:
 *   1. Tag the parent product with the new tag (e.g. requires-core-240-hub).
 *   2. Add an entry to CORE_MAP below with that tag and the core variant ID.
 *   3. (Optional) Add the tag to the dynamic-checkout disable check in the
 *      product template — see install instructions.
 * ========================================================================== */

(function () {
  'use strict';

  // ===========================================================
  // CONFIG — edit this block when adding new core mappings.
  // ===========================================================
  var CORE_MAP = {
    'requires-core-240': {
      variantId: 49970525307166,
      label: '240 Spindle Core'
    },
    'requires-core-240-hub': {
      variantId: 44723344277790,
      label: '240 Hub Core'
    },
    'requires-core-luxe-steer': {
      variantId: 21176312987726,
      label: 'Luxe Steer Core (Front Stock Spring Perch 240 Core PAIR)'
    },
    'requires-core-700-900': {
      variantId: 51793099194654,
      label: '700/900 Spindle Core'
    }
  };

  // ===========================================================
  // Internal — do not edit below unless you know what you're doing.
  // ===========================================================
  var AUTO_ADD_PROPERTY = '_auto_added_for';
  var VISIBLE_PROPERTY = 'Required core';
  var VISIBLE_PROPERTY_VALUE = 'Refundable on return';

  var CORE_VARIANT_IDS = {};
  Object.keys(CORE_MAP).forEach(function (tag) {
    CORE_VARIANT_IDS[CORE_MAP[tag].variantId] = true;
  });

  // Native fetch reference — used internally so our own calls don't recurse
  // through the wrapped fetch.
  var nativeFetch = window.fetch.bind(window);

  // Cache /products/HANDLE.js lookups
  var productCache = {};
  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return nativeFetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { productCache[handle] = p; return p; })
      .catch(function () { return null; });
  }

  // Returns { ok, core } for a product:
  //   ok=true,  core=CORE_MAP entry => product needs this core
  //   ok=true,  core=null            => product needs no core
  //   ok=false, core=null            => couldn't determine (fetch failed)
  // The ok flag matters: reconcile skips destructive actions (orphan
  // removal) when any lookup failed, so a transient network hiccup or
  // Cloudflare challenge doesn't make us delete real cart contents.
  function requiredCoreFor(item) {
    if (!item || !item.handle) return Promise.resolve({ ok: true, core: null });
    if (CORE_VARIANT_IDS[item.variant_id]) return Promise.resolve({ ok: true, core: null });
    return fetchProduct(item.handle).then(function (p) {
      if (!p) return { ok: false, core: null };
      var tags = Array.isArray(p.tags)
        ? p.tags
        : String(p.tags || '').split(',').map(function (t) { return t.trim(); });
      for (var i = 0; i < tags.length; i++) {
        var t = String(tags[i]).toLowerCase();
        if (CORE_MAP[t]) return { ok: true, core: CORE_MAP[t] };
      }
      return { ok: true, core: null };
    });
  }

  // Properties can come back as object or array depending on Shopify version.
  function getProperty(item, name) {
    var props = item && item.properties;
    if (!props) return null;
    if (Array.isArray(props)) {
      for (var i = 0; i < props.length; i++) {
        if (props[i].name === name) return props[i].value;
      }
      return null;
    }
    return props[name] || null;
  }

  function getCart() {
    return nativeFetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  // Both helpers return a Promise<boolean> indicating whether the API
  // call succeeded. We only flip changesMade=true on a confirmed-OK
  // response — otherwise a failing add (Cloudflare challenge, invalid
  // variant, etc.) would be reported as "we changed the cart" and
  // could feed an outer reload loop.
  function addCoreLine(core, parentVariantId, quantity) {
    var props = {};
    props[AUTO_ADD_PROPERTY] = String(parentVariantId);
    props[VISIBLE_PROPERTY] = VISIBLE_PROPERTY_VALUE;
    return nativeFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: core.variantId,
        quantity: quantity || 1,
        properties: props
      })
    }).then(function (r) {
      if (!r.ok) {
        try { console.warn('[requires-core] addCoreLine failed', r.status, core); } catch (e) {}
      }
      return r.ok;
    }).catch(function (err) {
      try { console.warn('[requires-core] addCoreLine error', err); } catch (e) {}
      return false;
    });
  }

  function changeLineQuantity(line, quantity) {
    return nativeFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (r) {
      if (!r.ok) {
        try { console.warn('[requires-core] changeLineQuantity failed', r.status, line, quantity); } catch (e) {}
      }
      return r.ok;
    }).catch(function (err) {
      try { console.warn('[requires-core] changeLineQuantity error', err); } catch (e) {}
      return false;
    });
  }

  // Serialize reconcile calls so rapid cart changes don't double-add.
  var reconcileLock = Promise.resolve();
  function reconcileCart() {
    var run = function () { return _reconcileCart().catch(function () {}); };
    reconcileLock = reconcileLock.then(run, run);
    return reconcileLock;
  }

  function _reconcileCart() {
    return getCart().then(function (cart) {
      // Map: parentVariantId -> { qty, core }
      var needed = {};
      var lookups = [];
      var allLookupsOk = true;
      cart.items.forEach(function (item) {
        if (CORE_VARIANT_IDS[item.variant_id]) return;
        lookups.push(
          requiredCoreFor(item).then(function (result) {
            if (!result.ok) { allLookupsOk = false; return; }
            if (!result.core) return;
            var key = String(item.variant_id);
            if (!needed[key]) needed[key] = { qty: 0, core: result.core };
            needed[key].qty += item.quantity;
          })
        );
      });

      return Promise.all(lookups).then(function () {
        // Existing auto-added core lines, grouped by parent
        var existing = {}; // parentVariantId -> [{ line, qty, variantId }]
        cart.items.forEach(function (item, idx) {
          if (!CORE_VARIANT_IDS[item.variant_id]) return;
          var parent = getProperty(item, AUTO_ADD_PROPERTY);
          if (!parent) return; // standalone core sale — leave it alone
          if (!existing[parent]) existing[parent] = [];
          existing[parent].push({
            line: idx + 1,
            qty: item.quantity,
            variantId: item.variant_id
          });
        });

        var actions = Promise.resolve();
        var changesMade = false;

        // For each parent that needs a core, ensure correct presence + qty.
        // Only set changesMade after a confirmed-successful API response.
        Object.keys(needed).forEach(function (parentKey) {
          var n = needed[parentKey];
          var lines = (existing[parentKey] || []).filter(function (e) {
            return e.variantId === n.core.variantId;
          });
          if (lines.length === 0) {
            actions = actions.then(function () {
              return addCoreLine(n.core, parentKey, n.qty).then(function (ok) {
                if (ok) changesMade = true;
              });
            });
          } else {
            var totalQty = lines.reduce(function (s, e) { return s + e.qty; }, 0);
            if (totalQty !== n.qty) {
              actions = actions.then(function () {
                return changeLineQuantity(lines[0].line, n.qty).then(function (ok) {
                  if (ok) changesMade = true;
                });
              });
            }
          }
        });

        // If any auto-added core has no matching parent in cart, remove it.
        // SAFETY: only do this when every product lookup succeeded. If any
        // lookup failed (network glitch, Cloudflare challenge, etc.), we
        // can't trust that "needed[parentKey] is empty" actually means the
        // parent doesn't need a core — it might just mean we couldn't read
        // the parent's tags. Skipping orphan removal in that case keeps us
        // from deleting good cart contents during a hiccup.
        if (allLookupsOk) {
          Object.keys(existing).forEach(function (parentKey) {
            if (needed[parentKey]) return;
            existing[parentKey].forEach(function (e) {
              actions = actions.then(function () {
                return getCart().then(function (fresh) {
                  for (var i = 0; i < fresh.items.length; i++) {
                    var it = fresh.items[i];
                    if (
                      it.variant_id === e.variantId &&
                      getProperty(it, AUTO_ADD_PROPERTY) === parentKey
                    ) {
                      return changeLineQuantity(i + 1, 0).then(function (ok) {
                        if (ok) changesMade = true;
                      });
                    }
                  }
                });
              });
            });
          });
        }

        return actions.then(function () { return changesMade; });
      });
    });
  }

  // ===========================================================
  // Wrap window.fetch — catches modern AJAX cart code.
  // Only matches /cart/add and /cart/change, NOT /cart/update or
  // /cart/clear. /cart/update is used by Shop Pay and other apps to
  // set cart attributes — reacting to those caused a reload loop.
  //
  // We do NOT auto-reload the page after reconciling, even on /cart.
  // Auto-reload was the engine of an earlier loop bug; the cart will
  // refresh naturally on the customer's next interaction. Themes
  // wanting to refresh a cart drawer can listen for 'cart:reconciled'.
  // ===========================================================
  var cartModifyRe = /\/cart\/(add|change)(\.js)?(\?|$)/;
  window.fetch = function (input, init) {
    var url = (typeof input === 'string') ? input : (input && input.url) || '';
    var p = nativeFetch.apply(window, arguments);
    if (!cartModifyRe.test(url)) return p;
    return p.then(function (response) {
      reconcileCart().then(function (changed) {
        document.dispatchEvent(new CustomEvent('cart:reconciled', { detail: { changed: !!changed } }));
      });
      return response;
    });
  };

  // ===========================================================
  // Wrap XMLHttpRequest — catches jQuery $.ajax and older code.
  // ===========================================================
  try {
    var XHR = window.XMLHttpRequest;
    var origOpen = XHR.prototype.open;
    var origSend = XHR.prototype.send;
    XHR.prototype.open = function (method, url) {
      this.__bne_url = url;
      return origOpen.apply(this, arguments);
    };
    XHR.prototype.send = function () {
      var url = this.__bne_url || '';
      if (cartModifyRe.test(url)) {
        this.addEventListener('load', function () {
          reconcileCart().then(function (changed) {
            document.dispatchEvent(new CustomEvent('cart:reconciled', { detail: { changed: !!changed } }));
          });
        });
      }
      return origSend.apply(this, arguments);
    };
  } catch (e) { /* ignore */ }

  // ===========================================================
  // Cart-row locking is intentionally a no-op for now.
  //
  // Earlier versions tried to hide the Remove link and disable the
  // qty input on auto-added cores. That code interacted poorly with
  // the theme's cart on /cart, so we've turned it off until we have
  // a safer approach. The reconcile-on-cart-change logic still
  // protects the cart: if a customer removes a core, the next
  // /cart/change.js triggers reconcile, which re-adds the core
  // (this is what Ben observed earlier as "auto reloads it").
  // ===========================================================
  function lockAutoAddedRows() { /* no-op */ }

  // ===========================================================
  // On every page load: reconcile + lock. No reload anywhere — the
  // cart UI will naturally re-render on the next user interaction.
  // ===========================================================
  function init() {
    reconcileCart().then(function () {
      lockAutoAddedRows();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();